import { useState } from 'react';
import { C } from '../theme';
import { Icon, Mark } from '../icons';
import { Loading, Empty } from '../components/States';
import { api } from '../api';
import type { Me, Property } from '../api';
import { useAsync } from '../useAsync';
import { useRoute, type Route } from '../useRoute';
import { ScopeChip } from './ScopeChip';
import { OwnerOverview } from './OwnerOverview';
import { OwnerRoster } from './OwnerRoster';
import { OwnerDetail } from './OwnerDetail';
import { RoomsPage } from './pages/RoomsPage';
import { LedgerPage } from './pages/LedgerPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { RemindersPage } from './pages/RemindersPage';
import { SettingsPage } from './pages/SettingsPage';

const NAV = [
  ['overview', 'grid', 'Overview'],
  ['rooms', 'home', 'Rooms'],
  ['tenancies', 'key', 'Tenancies'],
  ['ledger', 'ledger', 'Ledger'],
  ['collections', 'rupee', 'Collections'],
  ['documents', 'doc', 'Documents'],
  ['maintenance', 'wrench', 'Maintenance'],
  ['reminders', 'bell', 'Reminders'],
] as const;

type Screen = 'overview' | 'rooms' | 'tenancies' | 'detail' | 'ledger' | 'collections' | 'documents' | 'maintenance' | 'reminders' | 'settings';

/** Sheets a tenancy can deep-link straight into: #/tenancies/<id>/payment */
const SHEETS = ['edit', 'payment', 'invite'] as const;
type SheetName = (typeof SHEETS)[number];
const sheetFromRoute = (route: Route): SheetName | undefined =>
  (SHEETS as readonly string[]).includes(route.tab ?? '') ? (route.tab as SheetName) : undefined;

/** Map the URL route → the screen + selected tenancy this surface renders. */
function screenFromRoute(route: Route): { screen: Screen; tenancyId: string | null } {
  switch (route.section) {
    case 'tenancies':
      return route.id ? { screen: 'detail', tenancyId: route.id } : { screen: 'tenancies', tenancyId: null };
    case 'rooms':
    case 'ledger':
    case 'collections':
    case 'documents':
    case 'maintenance':
    case 'reminders':
    case 'settings':
      return { screen: route.section, tenancyId: null };
    default:
      return { screen: 'overview', tenancyId: null };
  }
}

export function OwnerApp({ me, onLogout, onMeChange }: { me: Me; onLogout: () => void; onMeChange?: (me: Me) => void }) {
  const { route, navigate, back } = useRoute();
  const { screen, tenancyId } = screenFromRoute(route);

  // Single-property UI (SPEC §10): resolve the owner's first property.
  const { data: properties, loading } = useAsync<Property[]>(() => api.properties(), []);
  const [propertyEdit, setPropertyEdit] = useState<Property | null>(null);
  const property = propertyEdit ?? properties?.[0] ?? null;

  const openTenancy = (id: string) => navigate(`#/tenancies/${id}`);
  /** Open a tenancy, optionally straight into one of its sheets. */
  const openTenancySheet = (id: string, sheet?: string) =>
    navigate(sheet && sheet !== 'open' ? `#/tenancies/${id}/${sheet}` : `#/tenancies/${id}`);

  const initials = (me.displayName ?? 'Owner')
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '236px 1fr', fontSize: 14, color: C.ink }}>
        {/* sidebar */}
        <nav style={{ background: C.side, padding: '20px 14px', display: 'flex', flexDirection: 'column', gap: 5, height: '100vh', position: 'sticky', top: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 6px 16px' }}>
            <Mark size={30} />
            <span style={{ fontWeight: 600, letterSpacing: '-.01em', color: '#fff', fontSize: 17 }}>Premise</span>
          </div>
          {NAV.map(([key, icon, labelText]) => {
            const on = screen === key || (key === 'tenancies' && screen === 'detail');
            return (
              <button
                key={key}
                onClick={() => navigate(`#/${key}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', borderRadius: 9, border: 0, cursor: 'pointer', background: on ? 'rgba(255,255,255,.12)' : 'transparent', color: on ? '#fff' : 'rgba(255,255,255,.66)', fontWeight: 500, fontSize: 14.4, textAlign: 'left' }}
              >
                <Icon n={icon} size={18} /> {labelText}
              </button>
            );
          })}
          <div style={{ marginTop: 'auto' }} />
          <button onClick={() => navigate('#/settings')} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', borderRadius: 9, border: 0, background: screen === 'settings' ? 'rgba(255,255,255,.12)' : 'transparent', color: screen === 'settings' ? '#fff' : 'rgba(255,255,255,.66)', fontWeight: 500, fontSize: 14.4, cursor: 'pointer' }}>
            <Icon n="gear" size={18} /> Settings
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderTop: '1px solid rgba(255,255,255,.12)', marginTop: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 999, background: 'rgba(255,255,255,.16)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 12.5 }}>{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ color: '#fff', fontSize: 13, display: 'block' }}>{me.displayName ?? 'Owner'}</b>
              <small style={{ display: 'block', color: 'rgba(255,255,255,.55)', fontSize: 11.5 }}>Owner</small>
            </div>
            <button onClick={onLogout} aria-label="Log out" title="Log out" style={{ border: 0, background: 'transparent', color: 'rgba(255,255,255,.55)', cursor: 'pointer', display: 'flex', padding: 4 }}>
              <Icon n="logout" size={16} />
            </button>
          </div>
        </nav>

        {/* main */}
        <main style={{ background: C.bg, padding: '26px 34px 44px', minHeight: '100vh', overflow: 'auto' }}>
          {/* property-scope chip (groundwork for a future switcher) */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
            <ScopeChip name={property?.name} />
          </div>
          {loading && screen !== 'detail' ? (
            <Loading label="Loading property…" minHeight={460} />
          ) : screen === 'detail' && tenancyId ? (
            <OwnerDetail tenancyId={tenancyId} onBack={() => back()} initialSheet={sheetFromRoute(route)} />
          ) : screen === 'tenancies' ? (
            <OwnerRoster propertyName={property?.name} onOpen={openTenancy} />
          ) : screen === 'overview' ? (
            property ? (
              <OwnerOverview propertyId={property.id} onOpen={openTenancy} onOpenSheet={openTenancySheet} />
            ) : (
              <Empty icon="grid" title="No property yet" hint="Once a property is added it will show here." minHeight={460} />
            )
          ) : screen === 'rooms' ? (
            <RoomsPage propertyId={property?.id} propertyName={property?.name} onOpen={openTenancy} />
          ) : screen === 'ledger' ? (
            <LedgerPage propertyId={property?.id} propertyName={property?.name} onOpen={openTenancy} />
          ) : screen === 'collections' ? (
            <CollectionsPage propertyName={property?.name} />
          ) : screen === 'documents' ? (
            <DocumentsPage propertyId={property?.id} propertyName={property?.name} />
          ) : screen === 'maintenance' ? (
            <MaintenancePage />
          ) : screen === 'reminders' ? (
            <RemindersPage propertyId={property?.id} propertyName={property?.name} />
          ) : screen === 'settings' ? (
            <SettingsPage me={me} property={property} onLogout={onLogout} onMeChange={onMeChange} onPropertyChange={setPropertyEdit} />
          ) : (
            <Empty icon="gear" title="Coming soon" minHeight={460} />
          )}
        </main>
    </div>
  );
}
